import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { Z_INDEX } from '@/constants/z-index';
// TODO: replace all Dashboard internal imports below with '@/features/dashboard' once Dashboard
// feature has a public index.ts (GO-2318 tracks this; Dashboard not yet refactored).
import { dashboardQueryKeys, useDashboard } from '@/features/Dashboard/api';
import { getDashboardApiUrl } from '@/features/Dashboard/api/dashboardUrl';
import { waitForApiSync } from '@/features/Dashboard/api/mutations';
import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { broadcastDashboardMutation } from '@/features/Dashboard/hooks/useDashboardSync';
import type { TabInfo } from '@/features/Dashboard/pages/Edit/components/menus/DashboardTargetMenu';
import { DashboardTargetMenu } from '@/features/Dashboard/pages/Edit/components/menus/DashboardTargetMenu';
import type { DashboardTab } from '@/features/Dashboard/types';
import { getUniqueDashboardName } from '@/features/Dashboard/utils/getUniqueDashboardName';
import { getUniqueTabName } from '@/features/Dashboard/utils/getUniqueTabName';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { http } from '@/services/http';
import Spinner from '@/shared/ui/feedback/Spinner';
import { Box } from '@/shared/ui/layout/Box';
import {
  clearPendingAddAppDrawer,
  requestAddAppDrawer,
  SELECTOR_MODES
} from '../../state/symbolSelector.slice';

interface CreateAppMenuProps {
  /** Selector container element — used as Popper anchor (bottom-end placement) */
  containerEl?: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectorMode: SELECTOR_MODES;
}

const MENU_BG_COLOR_SPOT = 'gx.highlight';
const MENU_BG_COLOR_CURVE = 'gx.selectorForwardCurve';

export const CreateAppMenu = ({ containerEl, open, onClose, selectorMode }: CreateAppMenuProps) => {
  const { data: allDashboards } = useDashboard();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const navigationTargetRef = useRef<{ dashboardName: string; tabName: string } | null>(null);

  const { mutate: createTab, isPending } = useMutation({
    mutationFn: async ({
      dashboardName,
      tabName,
      payload
    }: {
      dashboardName: string;
      tabName: string;
      payload: DashboardTab;
    }) => {
      navigationTargetRef.current = { dashboardName, tabName };
      await http.internal.post(getDashboardApiUrl(encodeURIComponent(dashboardName)), payload);
      await waitForApiSync();
    },
    onSuccess: async () => {
      broadcastDashboardMutation();
      await queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });
      if (navigationTargetRef.current) {
        const { dashboardName, tabName } = navigationTargetRef.current;
        navigateAndOpenDrawer(dashboardName, tabName);
        navigationTargetRef.current = null;
      }
    },
    onError: () => {
      navigationTargetRef.current = null;
    }
  });

  const navigateAndOpenDrawer = useCallback(
    (dashboardName: string, tabName: string) => {
      dispatch(clearPendingAddAppDrawer());
      navigate(`/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(tabName)}`);
      dispatch(requestAddAppDrawer());
    },
    [dispatch, navigate]
  );

  const handleSelectTab = useCallback(
    (dashboardName: string, tab: TabInfo) => {
      if (isPending) return;
      navigateAndOpenDrawer(dashboardName, tab.tabName);
      onClose();
    },
    [isPending, navigateAndOpenDrawer, onClose]
  );

  const handleCreateNewTab = useCallback(
    (dashboardName: string) => {
      if (isPending || !allDashboards) return;
      const existingTabs = (allDashboards[dashboardName] as DashboardTab | undefined) ?? {};
      const existingTabNames = Object.keys(existingTabs);
      const newTabName = getUniqueTabName(existingTabNames);
      const payload: DashboardTab = { ...existingTabs, [newTabName]: [] };
      createTab({ dashboardName, tabName: newTabName, payload });
      onClose();
    },
    [isPending, allDashboards, createTab, onClose]
  );

  const handleCreateNewDashboard = useCallback(() => {
    if (isPending) return;
    const existingNames = Object.keys(allDashboards || {});
    const newName = getUniqueDashboardName(existingNames);
    const payload: DashboardTab = { [TAB_DEFAULT_BASE_NAME]: [] };
    createTab({ dashboardName: newName, tabName: TAB_DEFAULT_BASE_NAME, payload });
    onClose();
    // Navigation is handled in the mutation's onSuccess callback;
    // returning undefined signals DashboardTargetMenu to take no further action.
  }, [isPending, allDashboards, createTab, onClose]);

  const getDashboardTabs = useCallback(
    (dashboardName: string) => {
      const tabs = allDashboards?.[dashboardName];
      if (!tabs) return null;
      return Object.keys(tabs).map(name => ({ tabId: name, tabName: name }));
    },
    [allDashboards]
  );

  const paperBgColor =
    selectorMode === SELECTOR_MODES.FORWARD_CURVE ? MENU_BG_COLOR_CURVE : MENU_BG_COLOR_SPOT;

  return (
    <>
      {isPending &&
        createPortal(
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: Z_INDEX.loadingBackdrop,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
          >
            <Spinner centered={false} />
          </Box>,
          document.body
        )}
      <DashboardTargetMenu
        anchorEl={containerEl ?? null}
        dashboards={Object.keys(allDashboards || {})}
        disabled={isPending}
        getTabs={getDashboardTabs}
        headerText='Select destination dashboard'
        offset={[0, 0]}
        open={open}
        paperSx={{
          backgroundColor: paperBgColor,
          width: 182,
          minWidth: 0
        }}
        placement='bottom-end'
        zIndexWhenBelow={Z_INDEX.priorityTop}
        zIndexWhenAbove={Z_INDEX.priorityLow}
        onClose={onClose}
        onCreateDashboard={handleCreateNewDashboard}
        onCreateNewTab={handleCreateNewTab}
        onSelectTab={(dashboardName, tab) => {
          handleSelectTab(dashboardName, tab);
        }}
      />
    </>
  );
};
